import { useLazyQuery, useMutation } from '@moonpig/web-core-graphql'
import { DesignIntent } from '@moonpig/web-shared-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import { GetDesignWithIntentsGQL } from '../../queries/getDesignWithIntent/getDesignWithIntents'
import {
  GetDesignWithIntentsQuery,
  GetDesignWithIntentsQueryVariables,
} from '../../queries/getDesignWithIntent/__generated__/getDesignWithIntents'
import { CreateDesignWithIntentsGQL } from '../../queries/getDesignWithIntent/createDesignWithIntents'
import {
  CreateDesignWithIntentsMutation,
  CreateDesignWithIntentsMutationVariables,
} from '../../queries/getDesignWithIntent/__generated__/createDesignWithIntents'
import { mapScenes } from './mapScenes'

export type CreateDesignWithIntentsProps = {
  cardId: string
  sku: string | null
  intents: DesignIntent[]
}

export type CreateDesignWithIntentsResponse = Promise<{
  id: string
} | null>

export const useCreateDesignWithIntents = () => {
  const [createDefaultDesignWithIntents] = useLazyQuery<
    GetDesignWithIntentsQuery,
    GetDesignWithIntentsQueryVariables
  >(GetDesignWithIntentsGQL)

  const [createDesign] = useMutation<
    CreateDesignWithIntentsMutation,
    CreateDesignWithIntentsMutationVariables
  >(CreateDesignWithIntentsGQL)

  const createDesignWithIntents = async ({
    cardId,
    sku,
    intents,
  }: CreateDesignWithIntentsProps): CreateDesignWithIntentsResponse => {
    const { data: defaultDesignData } = await createDefaultDesignWithIntents({
      variables: {
        productKey: cardId,
        intents,
      },
    })

    const designDefault = defaultDesignData?.designDefault

    if (designDefault?.__typename !== 'Design') {
      logger.fixToday(
        'Failed to get default design when applying design intents',
        {},
        designDefault?.message,
      )
      return null
    }

    const { data: createDesignData } = await createDesign({
      variables: {
        input: {
          productKey: cardId,
          sku,
          scenes: mapScenes(designDefault.scenes),
        },
      },
    })

    const createdDesign = createDesignData?.createDesign
    if (createdDesign?.__typename !== 'Design') {
      logger.fixToday(
        'Failed to create design with applied design intents',
        {},
        createdDesign?.message,
      )
      return null
    }

    return { id: createdDesign.id }
  }

  return { createDesignWithIntents }
}
